import './restrictedAccessModal.scss';

import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Button,
  ButtonVariant,
  Input,
  InputType,
  Modal,
  Size,
} from '@eliberty/design-system-react';

import { ResponsiveSize } from '~constants';
import { findModalPortal } from '~helpers';
import { useMediaQuery } from '~hooks';

interface RestrictedAccessModalProps {
  /** The open state of the modal */
  open: boolean;
  /** Display the invalid code error message */
  invalidCode?: boolean;
  /** The callback function called when the cancel button is clicked */
  onCancel: () => void;
  /** The callback function called when the code is submitted */
  onSubmit: (code: string | undefined) => void;
}

const RestrictedAccessModal = ({
  open,
  invalidCode = false,
  onCancel,
  onSubmit,
}: RestrictedAccessModalProps) => {
  const [code, setCode] = useState<string | undefined>(undefined);
  const isMobile = useMediaQuery(ResponsiveSize.SCREEN_S_MOBILE);
  const intl = useIntl();

  const handleCodeChange = () => {
    onSubmit(code);
  };

  return (
    <Modal
      isOpen={open}
      onClose={onCancel}
      title={intl.formatMessage({ defaultMessage: 'Restricted access', id: 'restrictedAccess' })}
      portalProps={{ target: findModalPortal() }}
      centered
    >
      <Modal.Body>
        <div className='RestrictedAccessModal_content'>
          <FormattedMessage
            id='accessCodeRequired'
            defaultMessage='This exclusive offer requires the access code provided by your organization.'
          />

          <Input
            label={intl.formatMessage({
              defaultMessage: 'Code',
              id: 'code',
            })}
            name='code'
            value={code}
            onChange={setCode}
            type={InputType.Text}
            leadingIconName='action-lock'
            errorText={
              invalidCode
                ? intl.formatMessage({
                    defaultMessage: 'This code is not valid',
                    id: 'invalidProductCode',
                  })
                : undefined
            }
            helperIconName='status-infoCircle'
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type='button'
          size={Size.M}
          fullWidth={isMobile}
          variant={ButtonVariant.Outlined}
          onClick={onCancel}
          data-testid='RestrictedAccessModal_cancelButton'
        >
          <FormattedMessage defaultMessage='Cancel' id='cancel' />
        </Button>
        <Button
          type='submit'
          size={Size.M}
          fullWidth={isMobile}
          disabled={!code}
          onClick={handleCodeChange}
          data-testid='RestrictedAccessModal_submitButton'
        >
          <FormattedMessage defaultMessage='Access offer' id='accessOffer' />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RestrictedAccessModal;
